import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomerSeatMap from '../../components/seatmap/CustomerSeatMap';
import api from '../../services/api';

export default function MobileSeatMapPage() {
  const { eventId } = useParams();

  // Mobile app passes user's JWT as ?token=... so the WebView can call auth-protected APIs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) localStorage.setItem('token', token);
  }, []);

  const [event, setEvent]                 = useState(null);
  const [seatMap, setSeatMap]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [gaSelections, setGaSelections]   = useState({});
  const [booking, setBooking]             = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [evRes, mapRes] = await Promise.all([
          api.get(`/events/${eventId}`),
          api.get(`/events/${eventId}/seat-map`),
        ]);
        setEvent(evRes.data?.data);
        setSeatMap(mapRes.data?.seat_map);
      } catch {
        toast.error('Failed to load seat map');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [eventId]);

  const getSeatSection = (seat) =>
    seatMap?.sections?.find(sec => (sec.seats || []).some(s => s.id === seat.id));

  const handleSeatToggle = useCallback((seat) => {
    setSelectedSeats(prev =>
      prev.some(s => s.id === seat.id)
        ? prev.filter(s => s.id !== seat.id)
        : [...prev, seat]
    );
  }, []);

  const handleGaChange = useCallback((sectionId, qty) => {
    setGaSelections(prev => ({ ...prev, [sectionId]: qty }));
  }, []);

  const totalTickets =
    selectedSeats.length +
    Object.values(gaSelections).reduce((sum, q) => sum + (q || 0), 0);

  const totalPrice = (() => {
    let t = 0;
    for (const seat of selectedSeats) {
      t += parseFloat(seat.effective_price ?? seat.price_override ?? 0);
    }
    if (seatMap) {
      for (const [secId, qty] of Object.entries(gaSelections)) {
        if (!qty) continue;
        const sec = seatMap.sections?.find(s => String(s.id) === String(secId));
        const price = sec?.price_zone?.price ?? sec?.default_price ?? 0;
        t += parseFloat(price) * qty;
      }
    }
    return t;
  })();

  async function handleBook() {
    if (totalTickets === 0) return;
    setBooking(true);
    try {
      const res = await api.post(`/events/${eventId}/seat-map/hold`, {
        seat_ids: selectedSeats.map(s => s.id),
        ga_items: Object.entries(gaSelections)
          .filter(([, q]) => q > 0)
          .map(([section_id, quantity]) => ({
            section_id: parseInt(section_id),
            quantity,
          })),
      });
      const payUrl = res.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('Could not get payment link');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setBooking(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={s.center}>
        <div style={{ textAlign: 'center', color: '#6B7280' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🎭</div>
          <div style={{ fontSize: 14 }}>Loading seat map…</div>
        </div>
      </div>
    );
  }

  if (!seatMap) {
    return (
      <div style={s.center}>
        <div style={{ textAlign: 'center', color: '#6B7280', fontSize: 14 }}>
          No seat map available for this event.
        </div>
      </div>
    );
  }

  const activeGa = Object.entries(gaSelections).filter(([, q]) => q > 0);
  const hasSelection = totalTickets > 0;

  // ── Main ──────────────────────────────────────────────────────────────────
  return (
    <div style={s.root}>
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTitle}>{event?.title || 'Seat Map'}</div>
        {event?.location && <div style={s.headerSub}>{event.location}</div>}
      </div>

      {/* Canvas */}
      <div style={s.canvas}>
        <CustomerSeatMap
          seatMap={seatMap}
          selectedSeats={selectedSeats}
          onSeatToggle={handleSeatToggle}
          gaSelections={gaSelections}
          onGaChange={handleGaChange}
        />
      </div>

      {/* Legend */}
      <div style={s.legend}>
        {[['#22C55E', 'Available'], ['#FBBF24', 'Selected'], ['#EF4444', 'Sold']].map(([color, label]) => (
          <div key={label} style={s.legendItem}>
            <div style={{ ...s.legendDot, background: color }} />
            {label}
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div style={s.bottomBar}>
        {!hasSelection ? (
          <div style={s.emptyHint}>Tap seats or sections to select</div>
        ) : (
          <>
            {/* ── Scrollable selection chips ── */}
            <div style={s.chipsRow}>

              {/* Seat badges */}
              {selectedSeats.map(seat => {
                const sec = getSeatSection(seat);
                const price = parseFloat(seat.effective_price ?? seat.price_override ?? 0);
                return (
                  <div key={seat.id} style={s.seatBadge}>
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                      <span style={s.seatBadgeText}>
                        {sec ? `${sec.name} · ` : ''}{seat.label}
                      </span>
                      {price > 0 && (
                        <span style={s.seatBadgePrice}>QAR {price.toFixed(2)}</span>
                      )}
                    </div>
                    <button
                      style={s.seatBadgeX}
                      onClick={() => handleSeatToggle(seat)}
                      aria-label="Remove seat"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}

              {/* GA section cards */}
              {activeGa.map(([secId, qty]) => {
                const sec = seatMap.sections?.find(s => String(s.id) === String(secId));
                const remaining = sec?.remaining_capacity ?? sec?.capacity ?? 0;
                const price = sec?.price_zone?.price ?? sec?.default_price ?? 0;
                return (
                  <div key={secId} style={s.gaCard}>
                    <div style={s.gaCardLabels}>
                      <span style={s.gaCardName}>{sec?.name || 'GA Zone'}</span>
                      <span style={s.gaCardSub}>
                        GA{price ? ` · QAR ${price}` : ''}
                      </span>
                    </div>
                    <div style={s.gaStepper}>
                      <button
                        style={s.gaStepBtn}
                        onClick={() => handleGaChange(secId, Math.max(0, qty - 1))}
                      >
                        −
                      </button>
                      <span style={s.gaQty}>{qty}</span>
                      <button
                        style={s.gaStepBtn}
                        onClick={() => handleGaChange(secId, Math.min(remaining, qty + 1))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Total + Book Now row ── */}
            <div style={s.bookRow}>
              <div>
                <div style={s.ticketCount}>
                  {totalTickets} ticket{totalTickets !== 1 ? 's' : ''}
                </div>
                <div style={s.totalPrice}>QAR {totalPrice.toFixed(2)}</div>
              </div>
              <button
                onClick={handleBook}
                disabled={booking}
                style={{ ...s.bookBtn, opacity: booking ? 0.6 : 1 }}
              >
                {booking ? 'Booking…' : 'Book Now'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    background: '#F3F4F6',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    overflow: 'hidden',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100dvh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  // Header
  header: {
    background: '#fff',
    borderBottom: '1px solid #E5E7EB',
    padding: '12px 16px',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSub: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },

  // Canvas
  canvas: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },

  // Legend
  legend: {
    display: 'flex',
    gap: 16,
    padding: '5px 16px',
    background: '#fff',
    borderTop: '1px solid #F3F4F6',
    flexShrink: 0,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 11,
    color: '#6B7280',
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },

  // Bottom bar shell
  bottomBar: {
    background: '#fff',
    borderTop: '1px solid #E5E7EB',
    padding: '10px 16px 14px',
    flexShrink: 0,
  },
  emptyHint: {
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: 14,
    padding: '6px 0',
  },

  // Chips strip
  chipsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 10,
    scrollbarWidth: 'none',         // Firefox
    msOverflowStyle: 'none',        // IE
  },

  // Seat badge
  seatBadge: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#FBBF24',
    borderRadius: 20,
    paddingLeft: 12,
    paddingRight: 10,
    paddingTop: 6,
    paddingBottom: 6,
  },
  seatBadgeText: {
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  seatBadgePrice: {
    fontSize: 10,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
  },
  seatBadgeX: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.8)',
    fontSize: 11,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },

  // GA card
  gaCard: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#F3F4F6',
    border: '1px solid #E5E7EB',
    borderRadius: 14,
    paddingLeft: 12,
    paddingRight: 10,
    paddingTop: 6,
    paddingBottom: 6,
  },
  gaCardLabels: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.3,
  },
  gaCardName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    whiteSpace: 'nowrap',
  },
  gaCardSub: {
    fontSize: 10,
    color: '#D97706',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  gaStepper: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  gaStepBtn: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1.5px solid #D1D5DB',
    background: '#fff',
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#374151',
    fontWeight: 700,
    padding: 0,
  },
  gaQty: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    minWidth: 18,
    textAlign: 'center',
  },

  // Total + book row
  bookRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ticketCount: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: 500,
  },
  totalPrice: {
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
    marginTop: 1,
  },
  bookBtn: {
    background: '#FBBF24',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    minWidth: 130,
    transition: 'opacity 0.15s',
  },
};
